import React, {FC, useEffect, useMemo, useRef, useState} from "react";
import {useApi} from "../api/APIContext";
import {useModal, useModalControls} from "@traas/lib/src/components/layout/ModalProvider";
import {TaskAttachment} from "../../../lib/src/api/dto/TaskAttachment"
import {TaskSupplier} from "../../../lib/src/api/dto/TaskSupplier"
import {Task, TaskStatus} from "../../../lib/src/api/dto/Task";
import {
  faCancel,
  faCheck,
  faCircleNotch,
  faNoteSticky,
  faPencil,
  faPlus,
  faSave,
  faSpinner,
  faTimes,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import {Button, IconButton} from "@traas/lib/src/components/form/Button";
import {Input} from "@traas/lib/src/components/form/Input";
import {Autocomplete} from "@traas/lib/src/components/form/Autocomplete"
import {useThemes} from "../planning/util/theming";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useTaskTypes} from "../planning/hooks/useTaskTypes";
import {useApiCall} from "../api/api";
import {Relation} from "@traas/lib/src/api/dto/Relation";
import {Order} from "@traas/lib/src/api/dto/Order";
import {Select} from "@traas/lib/src/components/form/Select";
import {AddOrderModal} from "./AddOrderModal";
import {VatRate} from "@traas/lib/src/api/dto/VatRate";
import {Ledger} from "@traas/lib/src/api/dto/Ledger";
import {EditOrderModal} from "./EditOrderModal";

export const TaskDetailsModal: FC<{task: Task}> = (p) => {
  const { close } = useModalControls()
  const {tasks, projects} = useApi()
  const {getTheme} = useThemes()

  const task = useMemo(() => {
    return tasks.find(t => t.id === p.task.id)!
  }, [tasks, p.task])

  const project = useMemo(() => {
    return projects.find(project => project.id === p.task.projectId)
  }, [projects, p.task])

  const {getTaskColor} = useTaskTypes()
  const {bg} = getTheme(getTaskColor(task) ?? undefined, task.status === TaskStatus.invoiced)

  return <form>
    <div className={"space-y-2"}>
      <div className={""}>
        <div className={`${bg} text-white text-sm px-4 py-1 rounded-lg mb-4`}>
          <div>{task.description}</div>
          {project &&
            <div className={"text-xs opacity-75"}>#{project.projectNumber}, {project.deliveryCity}</div>}
        </div>
        <h2 className={"mt-4 mb-1"}>Afhankelijkheden (Leveranciers)</h2>
        <SupplierManager task={task}/>
        {task.relatedSuppliers.length > 0 && <>
          <SupplierProjectManager task={task}/>
        </>}
        <h2 className={"mt-4"}>Werkbon opmerkingen voor medewerkers</h2>
        <div className={"grid grid-cols-1 lg:grid-cols-1 gap-3"}>
          <WorkorderRemarkEditor task={task}/>
          {task.projectId === null ? '' : <>
            <ProjectRemarkEditor task={task}/>
          </>}
        </div>
        <div className={'grid grid-cols-1'}>
          <TaskAttachmentsEditor task={task} />
        </div>

        <h2 className={"mt-4"}>Acties vereist</h2>

      </div>
      <footer className={"-mx-4 px-4 mt-3 py-3 border-t border-slate-100 flex items-center space-x-4"}>
        <Button type={'secondary'} size={'md'} text={'Sluiten'} onClick={() => close()}/>
      </footer>
    </div>
  </form>
}

const SupplierManager: FC<{ task: Task }> = ({task}) => {
  const {relations, orders, vatRates, ledgers} = useApi()

  const supplierRelations = useMemo(() => {
    return relations.filter((r) => r.isSupplier)
  }, [relations])

  const [isEditing, setIsEditing] = useState(false)
  const [description, setDescription] = useState<string>('')

  useEffect(() => {
    if (!isEditing && description.length > 0) {
      setDescription('')
    }
  }, [isEditing, description]);

  return <div className={"border border-slate-200 rounded-lg"}>
    {task.suppliers.length === 0 && <p className={'flex items-center justify-center pt-2'}>Deze taak heeft geen afhankelijkheden.</p>}
    <table className={`w-full max-w-full`}>
      <tbody>
        {task.suppliers.map((supplier, i) => {
          const relation = relations.find(sr => sr.id === supplier.relationId)
          return <SupplierManagerRow key={i} task={task} supplier={supplier} allSuppliers={relations} relation={relation!} orders={orders} vatRates={vatRates} ledgers={ledgers} />
        })}
        <AddSupplierRowEditor task={task} suppliers={supplierRelations} orders={orders} />
      </tbody>
    </table>
  </div>
}

const SupplierManagerRow: FC<{ task: Task, supplier: TaskSupplier, allSuppliers: Relation[], relation: Relation, orders: Order[], vatRates: VatRate[], ledgers: Ledger[] }> = ({ task, supplier, allSuppliers, relation, orders, vatRates, ledgers }) => {
  const cellStyle = `px-2 py-2 border-b border-slate-200 whitespace-nowrap`
  const order = orders.find(o => o.id === supplier.orderId)
  return <tr>
    <td className={cellStyle + " text-sm font-medium pr-4 w-48"}>{relation.name}</td>
    <td className={cellStyle + " text-sm overflow-hidden"}><EditSupplierRowEditor task={task} taskSupplier={supplier} orders={orders} suppliers={allSuppliers} vatRates={vatRates} ledgers={ledgers} /></td>
    <td className={cellStyle + " w-20"}>
      {supplier.orderId && order ? <OrderTrafficLight s={supplier} task={task} order={order} allSupplier={allSuppliers} vatRates={vatRates} ledgers={ledgers} />
        : <TrafficLight s={supplier} task={task}/>}
    </td>
  </tr>
}


const SupplierProjectManager: FC<{ task: Task }> = ({task}) => {
  const {suppliers, tasks, relations, orders, vatRates, ledgers} = useApi()

  const enrichedRelatedSuppliers = useMemo(() => {
    return task.relatedSuppliers
      .map(rs => {
        const details = suppliers.find(s => s.id === rs.supplierId)
        const relatedTaskDetails = tasks.find(t => t.id === rs.taskId)
        return {
          ...rs,
          supplier: details,
          relatedTask: relatedTaskDetails,
        }
      })
      .filter((rs) => rs.relatedTask?.id !== task.id)
      .sort((a,b) => {
        if (! a.relatedTask?.startAt && ! b.relatedTask?.startAt) {return 0;}
        if (! a.relatedTask?.startAt && !! b.relatedTask?.startAt) {return 1;}
        if (!! a.relatedTask?.startAt && ! b.relatedTask?.startAt) {return -1;}
        return a.relatedTask!.startAt!.getTime() > b.relatedTask!.startAt!.getTime() ? 1 : -1;
      })
  }, [task.relatedSuppliers, tasks, suppliers])

  return <div>
    {enrichedRelatedSuppliers.length > 0 && <>
    <h2 className={"mt-4 mb-1"}>Project Afhankelijkheden (Leveranciers)</h2>
    <div className={"border border-slate-200 rounded-lg"}>
      <table className={"w-full max-w-full"}>
        <tbody>
        {enrichedRelatedSuppliers.map((supplier, i) => {
          const relation = relations.find(s => s.id === supplier.relationId)
          return <SupplierManagerRow key={i} task={task} allSuppliers={relations} supplier={supplier} relation={relation!} orders={orders} vatRates={vatRates} ledgers={ledgers} />
        })}
        </tbody>
      </table>
    </div>
    </>}
  </div>
}

const OrderTrafficLight: FC<{s: TaskSupplier, task: Task, order: Order, allSupplier: Relation[], vatRates: VatRate[], ledgers: Ledger[] }> = ({ s, task, order, allSupplier, vatRates, ledgers }) =>  {
  const editOrderModal = useModal({title: 'Bestelling bewerken', body: <EditOrderModal order={order} ledgers={ledgers} task={task} vatRates={vatRates} relations={allSupplier} />, size: 'md'})

  return <div>
    <div className={`rounded-full p-1 flex space-x-1`}>
      <button type={'button'} onClick={() => editOrderModal.open()} className={`w-4 h-4 rounded-full border-2 ${s.status === "red" ? 'bg-red-600 border-red-600' : 'bg-transparent border-red-400'}`}></button>
      <button type={'button'} onClick={() => editOrderModal.open()} className={`w-4 h-4 rounded-full border-2 ${s.status === "orange" ? 'bg-amber-600 border-amber-600' : 'bg-transparent border-amber-400'}`}></button>
      <button type={'button'} onClick={() => editOrderModal.open()} className={`w-4 h-4 rounded-full border-2 ${s.status === "green" ? 'bg-green-600 border-green-600' : 'bg-transparent border-green-400'}`}></button>
    </div>
  </div>
}

const TrafficLight: FC<{s: TaskSupplier, task: Task}> = props => {
  const {updateTaskSupplierStatus} = useApi()
  const [status, setStatus] = useState(props.s.status)
  useEffect(() => {
    setStatus(props.s.status)
  }, [props.s.status]);


  const updateStatus = async (s: "red"|"orange"|"green") => {
    setStatus(s)
    await updateTaskSupplierStatus(props.task.id, props.s.id, s)
  }

  return <div className={`rounded-full p-1 flex space-x-1`}>
    <button type={'button'} onClick={() => updateStatus('red')} className={`w-4 h-4 rounded-full border-2 ${status === "red" ? 'bg-red-600 border-red-600' : 'bg-transparent border-red-400'}`}></button>
    <button type={'button'} onClick={() => updateStatus('orange')} className={`w-4 h-4 rounded-full border-2 ${status === "orange" ? 'bg-amber-600 border-amber-600' : 'bg-transparent border-amber-400'}`}></button>
    <button type={'button'} onClick={() => updateStatus('green')} className={`w-4 h-4 rounded-full border-2 ${status === "green" ? 'bg-green-600 border-green-600' : 'bg-transparent border-green-400'}`}></button>
  </div>
}

const EditSupplierRowEditor: FC<{taskSupplier: TaskSupplier, task: Task, orders: Order[], suppliers: Relation[], vatRates: VatRate[], ledgers: Ledger[] }> = ({ task, taskSupplier, orders, suppliers, vatRates, ledgers }) => {
  const {updateTaskSupplier, deleteTaskSupplier} = useApi()

  const filteredOrders = orders.filter((o) => o.supplierRelationId === taskSupplier.relationId && o.projectId === task.projectId)
  const orderOptions: { [key: string]: string } = {
    '-': '- Niet gekoppeld -',
    ...Object.fromEntries(
      filteredOrders
        .sort((a, b) => a.orderNumber.localeCompare(b.orderNumber))
        .map(o => [o.id, `${o.orderNumber} - ${o.description}`])
    ),
  };

  const [isEditing, setIsEditing] = useState(false)
  const [order, setOrder] = useState<string>(taskSupplier.orderId ?? Object.keys(orderOptions)[0] ?? '-')
  const [description, setDescription] = useState(taskSupplier.description ?? '')

  const addOrderModal = useModal({title: 'Order toevoegen', body: <AddOrderModal task={task} relations={suppliers} vatRates={vatRates} ledgers={ledgers} onFinish={(order) => setOrder(order.id)} />, size: "md"})

  useEffect(() => {
    if (!isEditing) {
      setDescription(taskSupplier.description ?? '')
    }
  }, [taskSupplier, isEditing]);

  const save = async () => {
    await updateTaskSupplier(
      task.id,
      taskSupplier.id,
      taskSupplier.status,
      description,
      order !== '-' ? order : null
    )
    setIsEditing(false)
  }
  const del = async () => {
    await deleteTaskSupplier(task.id, taskSupplier.id)
    setIsEditing(false)
  }

  if (isEditing) {
    return <div>
      <div className={"h-8 flex justify-start items-center space-x-1 group"}>
        <div className={'text-sm font-medium w-32'}>Omschrijving:</div>
        <div className={'-mt-1'}>
          <Input type={'text'} label={''} value={description} onChange={setDescription} focus={true} />
        </div>
        <IconButton type={'primary'} size={'md'} icon={faSave} onClick={save} />
        <IconButton type={'secondary'} size={'md'} icon={faCancel} onClick={() => setIsEditing(false)} />
        <IconButton type={'danger'} size={'md'} icon={faTrash} onClick={del} />
      </div>
      <div className={'h-10 flex justify-start items-center space-x-1 mt-2'}>
        <div className={'text-sm font-medium w-32'}>Order:</div>
        <div className={'-mt-1'}>
          <Select label={''} options={orderOptions} value={order} onChange={setOrder} />
        </div>
        {/* TODO: Orders toevoegen heeft een offerte nodig. Dit later uitdenken hoe dit goed/overzichtelijk te doen is... */}
        {/*<IconButton type={'secondary'} size={'md'} icon={faPlus} onClick={() => {*/}
        {/*  addOrderModal.open()*/}
        {/*}} />*/}
      </div>
    </div>
  }

  return <div>
    <div className={"h-8 w-80 flex space-x-4 items-center group"}>
      {taskSupplier.orderId ? <div className={" text-sm overflow-hidden flex items-center flex-wrap"}>
        <div className={'bg-brand-100 px-3 py-0.5 rounded-xl text-xs text-brand-900'}>Order #{orders.find(o => o.id === taskSupplier.orderId)?.orderNumber}</div>
      </div> : <div className={" text-sm overflow-hidden"}></div>}
      <span className={"whitespace-normal flex-1"}>{description}</span>
      <button type={'button'} className={'ml-2'}>
        <FontAwesomeIcon icon={faPencil} className={"hover:bg-slate-200 group-hover:text-slate-700 rounded-xs w-3 h-3 p-2"} onClick={() => setIsEditing(true)} />
      </button>
    </div>

  </div>
}

const AddSupplierRowEditor: FC<{task: Task, suppliers: Relation[], orders: Order[] }> = ({ task, suppliers, orders }) => {
  const {addTaskSupplier} = useApi()

  const [isEditing, setIsEditing] = useState(false)
  const [supplier, setSupplier] = useState<string>('-')
  const [order, setOrder] = useState<string>('-')
  const [description, setDescription] = useState('')

  const supplierOptions: [string, string][] = [
    ['-', 'selecteer leverancier'],
    ...suppliers.map(s => [s.id, s.name] as [string, string]),
  ]

  const filteredOrders = orders.filter((o) => o.supplierRelationId === supplier && o.projectId === task.projectId)
  const orderOptions: { [key: string]: string } = {
    '-': '- Niet gekoppeld -',
    ...Object.fromEntries(
      filteredOrders
        .sort((a, b) => a.orderNumber.localeCompare(b.orderNumber))
        .map(o => [o.id, `${o.orderNumber} - ${o.description}`])
    ),
  };

  const save = async () => {
    await addTaskSupplier(
      task.id,
      supplier,
      description,
      'red',
      order !== '-' ? order : null
    )
    setIsEditing(false)
  }

  if (isEditing) {
    return <tr>
      <td className={"align-top py-1 pl-1 pr-4"}>
        <Autocomplete label="" options={supplierOptions} value={supplier} onChange={setSupplier}/>
      </td>

      <td className={"align-top py-1"}>
        <div className={"flex flex-col"}>
          <div className={"flex items-center gap-1"}>
            <div className={"w-32 shrink-0 text-sm font-medium"}>
              Omschrijving:
            </div>
            <div className={"flex-1"}>
              <Input type={"text"} label={""} value={description} onChange={setDescription} focus={true}/>
            </div>
            <IconButton type={"primary"} size={"md"} icon={faSave} onClick={save}/>
            <IconButton type={"secondary"} size={"md"} icon={faCancel} onClick={() => setIsEditing(false)}/>
          </div>
          <div className={'h-10 flex justify-start items-center space-x-1 mt-2'}>
            <div className={'text-sm font-medium w-32'}>Order:</div>
            <div className={'-mt-1'}>
              <Select label={''} options={orderOptions} value={order} onChange={setOrder} />
            </div>
          </div>
        </div>
      </td>
    </tr>
  }

  return <tr>
    <td className={'px-2 py-2'} colSpan={3}>
      <Button type={'secondary'} size={"xs"} text={'Afhankelijkheid toevoegen'} icon={faPlus} onClick={() => setIsEditing(true)} />
    </td>
  </tr>
}



const WorkorderRemarkEditor: FC<{task: Task}> = ({task}) => {
  const {updateTaskMetadata} = useApi()
  return <NoteEditor label={'Opmerkingen op Werkbon'} text={task.workorderRemark} saveText={async (newText) => {
    await updateTaskMetadata(task.id, {workorderRemark: newText})
  }} />
}
const ProjectRemarkEditor: FC<{task: Task}> = ({task}) => {
  const {updateTaskMetadata} = useApi()
  return <NoteEditor label={'Opmerkingen op Project'} text={task.projectRemark} saveText={async (newText) => {
    await updateTaskMetadata(task.id, {projectRemark: newText})
  }} />
}

const NoteEditor: FC<{text?: string|null, saveText: (newText: string) => Promise<void>, label: string}> = props => {
  const [mode, setMode] = useState<"view"|"edit">("view")
  const [viewText, setViewText] = useState(props.text ?? '')
  const [text, setText] = useState(props.text ?? '')

  const edit = () => {
    setText(props.text ?? '')
    setMode("edit")
  }
  const save = async () => {
    await props.saveText(text)
    setViewText(text)
    setMode("view")
  }
  useEffect(() => {
    setViewText(props.text ?? '')
  }, [props.text]);
  return <div className={"border border-slate-200 rounded-sm p-2 my-2"}>
    <div className={"flex items-center justify-between"}>
      <span className={"flex items-center text-xs font-bold text-blue-900"}>
        <FontAwesomeIcon icon={faNoteSticky} className={"text-blue-900 text-xs mr-2"} />
        {props.label}
      </span>
    </div>
    {mode === "view" && <button type={'button'} className={"text-xs text-slate-600 mt-2 p-2 rounded-sm flex w-full hover:bg-slate-50"} onClick={() => edit()}>
      <pre className={'flex-1 text-left font-sans whitespace-pre-wrap'}>{viewText}</pre>
      <FontAwesomeIcon icon={faPencil} className={"text-slate-600 text-xs mr-2"} />
    </button>}
    {mode === "edit" && <div>
      <textarea className={"outline-hidden bg-slate-100 rounded-sm mt-2 mb-1 p-2 text-sm w-full min-h-40"} value={text} onChange={(e) => setText(e.target.value)} />
      <div className={"flex space-x-2"}>
        <Button type={'primary'} size={'xs'} text={'Opslaan'} onClick={() => save()} />
        <Button type={'secondary'} size={'xs'} text={'Annuleren'} onClick={() => setMode("view")} />
      </div>
    </div>}
  </div>
}

const TaskAttachmentsEditor: FC<{ task: Task }> = (props) => {
  const {addMediaToWorkorder, deleteMediaFromWorkorder} = useApiCall()
  const [loading, setLoading] = useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true)
    await Promise.all((e.target.files ? [...e.target.files] : []).map(async (file) => {
      await addMediaToWorkorder(props.task.id, file)
    }))
    setLoading(false)
  };
  const [imagePage, setImagePage] = React.useState(0)
  useEffect(() => {
    if (props.task.taskAttachments.length < (imagePage + 1)) {
      setImagePage(Math.max(0, props.task.taskAttachments.length - 1))
    }
  }, [imagePage, props.task.taskAttachments]);

  if (loading) {
    return <>
      <div className={"border border-slate-200 rounded-lg p-4 flex flex-col items-center justify-center text-sm space-y-2"}>
        <FontAwesomeIcon icon={faSpinner} className={'animate-spin text-3xl'} />
      </div>
    </>
  }
    return <>
    {props.task.taskAttachments.length <= 0 ?
      <>
        <h2>Werkbon bijlagen</h2>
        <div className={"border border-slate-200 rounded-lg p-4 flex flex-col items-center justify-center text-sm space-y-2"}>
          <p>Deze taak heeft geen bijlage.</p>
          <div>
            <Button type={"secondary"} size={"sm"} text={"Bijlage toevoegen"} icon={faPlus}
                    onClick={() => fileInputRef.current?.click()}/>
            <input type={'file'} multiple ref={fileInputRef} onChange={handleFileChange} className={'hidden'}
                   accept={"image/*, .gif"}/>
          </div>
        </div>
      </>
      :
      <>
        {props.task.taskAttachments?.[imagePage] && <>
          <h2 className={"font-medium"}>Werbon bijlage:</h2>
          <div className={"py-2"}>
            <TaskImage attachment={props.task.taskAttachments?.[imagePage]}/>
          </div>
          <div className={"-mx-3 px-3 mt-2 border-t border-slate-200 flex items-center justify-between"}>
            <div>
              {props.task.taskAttachments?.map((_, k) => {
                return <button type={'button'} key={k}
                               className={`h-6 w-6 mt-1 mr-1 rounded-full text-sm font-medium border ${k === imagePage ? 'border-blue-900 bg-blue-900 text-white' : 'border-slate-200 text-blue-600'}`}
                               onClick={() => setImagePage(k)}>{k + 1}</button>
              })}
            </div>
            <div className={"flex space-x-3"}>
              <Button type={"secondary"} size={"sm"} text={"Bijlage toevoegen"} icon={faPlus}
                      onClick={() => fileInputRef.current?.click()}/>
              <input type={'file'} multiple ref={fileInputRef} onChange={handleFileChange} className={'hidden'}
                     accept={"image/*, .gif"}/>
              <Button type={"danger"} size={"sm"} text={"Bijlage verwijderen"} icon={faTrash}
                      onClick={() => deleteMediaFromWorkorder(props.task.id, props.task.taskAttachments[imagePage].id)}/>
            </div>

          </div>

          <div className={'border border-slate-200 px-3 py-1 rounded-sm my-3'}></div>
        </>}
      </>
      }
    </>

}

const TodoEditor: FC<{task: Task}> = ({task}) => {
  const {updateTaskMetadata} = useApi()
  return <TodosEditor todos={task.todos} saveTodos={async (newTodos) => {
    await updateTaskMetadata(task.id, {todos: newTodos})
  }} />
}
const TodosEditor: FC<{todos: {[text: string]: boolean}, saveTodos: (newTodos: {[text: string]: boolean}) => Promise<void>}> = ({todos, saveTodos}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [description, setDescription] = useState('')
  const submit = async () => {
    const newTodos = {...todos}
    newTodos[description] = false
    await saveTodos(newTodos)
    setDescription('')
    setIsEditing(false)
  }
  const check = async (item: string) => {
    const newTodos = {...todos}
    newTodos[item] = !todos[item]
    await saveTodos(newTodos)
  }
  const del = async (item: string) => {
    const newTodos = {...todos}
    delete newTodos[item]
    await saveTodos(newTodos)
  }
  return <div className={"text-sm border border-slate-200 rounded-sm mt-2 px-2"}>
    {Object.entries(todos).map(([text, value], i) => {
      return <TodoRow key={i} text={text} value={value} toggle={async () => check(text)} del={() => del(text)} />
    })}
    {Object.entries(todos).length === 0 && <div className={"text-sm text-slate-600 text-center py-4"}>
      Geen acties vereist.
    </div>}
    {isEditing ? <div className={"flex items-center"}>
      <div className={"pb-1 mr-2 flex-1"}>
        <Input type={'text'} label={''} value={description} onChange={setDescription} />
      </div>
      <div className={"flex space-x-1"}>
        <IconButton type={'primary'} size={'sm'} icon={faPlus} onClick={submit} />
        <IconButton type={'danger'} size={'sm'} icon={faCancel} onClick={() => setIsEditing(false)} />
      </div>
    </div> : <>
      <div className={'px-2 py-2'}>
        <Button type={'secondary'} size={"xs"} text={'Vereiste actie toevoegen'} icon={faPlus} onClick={() => setIsEditing(true)} />
      </div>
    </>}
  </div>
}
const TodoRow: FC<{text: string, value: boolean, toggle: () => Promise<void>, del: () => Promise<void>}> = props => {
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const del = async () => {
    setDeleting(true)
    await props.del()
    setDeleting(false)
  }
  const submit = async () => {
    setLoading(true)
    await props.toggle()
    setLoading(false)
  }
  return <div className={"border-b border-slate-200 cursor-pointer hover:bg-slate-50 -mx-2 flex items-stretch group"}>
    <button className={"flex items-center flex-1 px-3 py-2"} disabled={loading} onClick={submit}>
      <div className={`h-6 w-6 flex items-center justify-center rounded-full mr-3 border border-slate-200 ${props.value ? 'bg-blue-900 text-white border-blue-900' : `border-slate-200 ${loading ? 'text-blue-900' : 'text-transparent group-hover:text-slate-400'}`}`}>
        <FontAwesomeIcon icon={loading ? faCircleNotch : faCheck} spin={loading} />
      </div>
      {props.text}
    </button>
    <div className={"flex items-center justify-center"}>
      <button type={'button'} disabled={deleting} className={'text-red-600 h-6 w-6 hover:bg-red-100 mx-2 rounded-sm '} onClick={del}><FontAwesomeIcon icon={deleting ? faCircleNotch : faTimes} spin={deleting} /></button>
    </div>
  </div>
}

const TaskImage: FC<{ attachment: TaskAttachment }> = props => {
  const imageUrl = ('url' in props.attachment ? props.attachment['url'] : '') as string
  const [zoomed, setZoomed] = React.useState(false)
  return <div>
    {zoomed && <div className={"fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center"} onClick={() => setZoomed(false)}>
      <img src={imageUrl} className={"rounded-lg max-h-full max-w-full cursor-zoom-out"}
           onClick={() => setZoomed(false)}/>
    </div>}
    <div className={'h-[400px] flex items-center justify-center'}>
      <img src={imageUrl} className={"rounded-lg max-h-full max-w-full cursor-zoom-in"} onClick={() => setZoomed(true)} />
    </div>
  </div>
}